import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import 'survey-core/i18n/traditional-chinese'
import { useSurveyParams } from '../context/SurveyParamsContext'
import { useProgressSaving } from '../hooks/useProgressSaving'
import {
  convertQuestionMappings,
  convertTriggers,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { userId, sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const userIdRef = useRef(null)
  useEffect(() => {
    if (userId) userIdRef.current = userId
  }, [userId])
  const requiresLogin = {
    login: userId ? true : false,
    title: '員工編號',
    placeholder: 'MA00000',
    alert: '員工編號有誤，請重新登入，謝謝',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})

  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  const visibleIfConditionsInit = null
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 篩選條件 → 任一條件成立直接結束問卷
  // q1=以上時間皆不方便 / q5=34歲以下或66歲以上 / q7 未選魚油 /
  // q9=非自己有吃且自己購買 / q10=今年才開始 / q11=這半年沒買 / q13=已沒在吃
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression:
        `({q1} anyof ["none"]) or ` +
        `({q5} = 1 or {q5} = 4) or ` +
        `({q7} notcontains [1]) or ` +
        `({q9} = 2 or {q9} = 3 or {q9} = 4) or ` +
        `({q10} = 4) or ` +
        `({q11} anyof ["none"]) or ` +
        `({q13} = 6)`,
    },
  ])

  // q12「過去半年內主要食用的魚油」選項承接 q11（複選）有勾選之產品
  const questionMappings = convertQuestionMappings({
    q11: 'q12',
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  useProgressSaving({
    userIdRef,
    surveyModel,
    convertedSurvey: myAgreeJson,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    sfdSamplerId,
  })

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 不符資格 / 額滿之中止結語
  const endMessage = `目前與您相同特性的樣本已額滿，沒辦法參與本次活動。<br>感謝您撥冗填答，祝您有美好的一天！`

  // 正常完成結語（含典通客服資訊）
  const completedMessage = `<div>再次感謝您的回答。若您符合參加座談會的資格，我們會電話連絡您。<br>祝您有個美好的一天。<br><br>若有調查相關問題，請洽典通市場調查公司<br>典通客服專線<br>02-23315133#819 陳小姐<br>02-23315133#839 蘇小姐<br>問卷主題：魚油保健食品座談會</div>`

  const endSurveyMsg = (survey) => {
    if (
      survey.getValue(q(1))?.[0] === 'none' ||
      survey.getValue(q(5)) === v(1) ||
      survey.getValue(q(5)) === v(4) ||
      !survey.getValue(q(7))?.includes(v(1)) ||
      survey.getValue(q(9)) === v(2) ||
      survey.getValue(q(9)) === v(3) ||
      survey.getValue(q(9)) === v(4) ||
      survey.getValue(q(10)) === v(4) ||
      survey.getValue(q(11))?.[0] === 'none' ||
      survey.getValue(q(13)) === v(6)
    ) {
      setCompletionMessage(endMessage)
    } else {
      setCompletionMessage(completedMessage)
    }
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || !!sfdSamplerId,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
